<template>
  <TInputNumber
    :model-value="currentFontSize"
    tooltip="Font size"
    :min="8"
    :max="200"
    unit="px"
    @update:model-value="setFontSize($event)"
  />
</template>

<script lang="ts" setup>
import {
  expandSelectionToWord,
  getActiveMarks,
} from "../_internal/cutomExtensions";
import { TInputNumber } from "@topol/twig-ui";
import { Editor } from "@tiptap/core";
import { computed, ref, watch } from "vue";

const props = defineProps<{
  editor: Editor;
}>();

const localFontSize = ref<number | null>(null);
const previousFontSize = ref<number>(0);

const editorFontSize = computed(() => {
  setPreviousFontSize();
  if (!props.editor) return 0;

  // Get marks including stored marks (for empty paragraphs after Enter)
  const marks = getActiveMarks(props.editor);
  const textStyle = marks.find((mark) => mark.type.name === "textStyle");
  if (textStyle?.attrs?.fontSize) {
    return parseInt(textStyle.attrs.fontSize);
  }

  const { state } = props.editor;
  const { $from } = state.selection;

  const nodeFont = $from.parent.attrs?.fontSize;
  if (nodeFont) {
    return parseInt(nodeFont);
  }

  // 3️⃣ Fallback: zjisti z DOM
  if (typeof window !== "undefined" && window.getSelection) {
    const selection = window.getSelection();
    if (selection && selection.anchorNode) {
      let node = selection.anchorNode as HTMLElement | null;

      if (node?.classList?.contains("tiptap") && node.firstElementChild) {
        node = node.firstElementChild as HTMLElement;
      }

      // Najdi rodiče, co je prvek
      while (node && node.nodeType !== Node.ELEMENT_NODE) {
        node = node.parentElement;
      }

      let parentEditor = node;
      let isInEditor = false;
      while (parentEditor) {
        if (parentEditor.classList.contains("tiptap-editor")) {
          isInEditor = true;
          break;
        }
        parentEditor = parentEditor.parentElement as HTMLElement | null;
      }

      if (node && isInEditor) {
        const style = window.getComputedStyle(node);
        return parseInt(style.fontSize) || 0;
      }
    }
  }
  return previousFontSize.value;
});

const currentFontSize = computed(
  () => localFontSize.value ?? editorFontSize.value
);

function setFontSize(size: number) {
  if (!props.editor || !props.editor.view || props.editor.isDestroyed) return;
  localFontSize.value = size;
  expandSelectionToWord(props.editor);
  // Apply to the editor's preserved selection WITHOUT calling .focus():
  // .focus() moves DOM focus back into the contenteditable, which steals
  // focus from the font-size input on every keystroke (TOPOL-tiptap bug).
  props.editor
    .chain()
    .setFontSize(size + "px")
    .run();
}

function setPreviousFontSize() {
  if (editorFontSize.value) previousFontSize.value = editorFontSize.value;
}

watch(
  () => props.editor.state,
  () => {
    if (!props.editor.isFocused) return;
    if (
      localFontSize.value &&
      !props.editor.isActive("textStyle", {
        fontSize: localFontSize.value + "px",
      })
    ) {
      localFontSize.value = null;
    }
  }
);
</script>
